import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { autoSignIn, getCurrentUser, signOut as amplifySignOut, signUp as amplifySignUp, confirmSignUp as amplifyConfirmSignUp, SignInInput, ConfirmSignUpInput } from 'aws-amplify/auth';
import { signIn as amplifySignIn } from 'aws-amplify/auth';
import { resendSignUpCode } from 'aws-amplify/auth';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { deleteUser as amplifyDeleteUser} from 'aws-amplify/auth';
import { resetPassword, confirmResetPassword, updatePassword, fetchAuthSession } from 'aws-amplify/auth';
import { deleteMyProfile, updateMyProfile } from '../services/profiles';
import { applyPendingLifecycleConsent, deleteLifecycleEmailData, recordWebActivity, rememberPendingLifecycleConsent } from '../services/lifecycleEmails';


type SignUpParameters = {
  username: string;
  password: string;
  preferred_username: string;
  given_name: string;
  family_name: string;
  lifecycleEmailOptIn?: boolean;
};

interface AuthContextType {
  isUserSignedIn: boolean;
  userDetails: any;
  signIn: (signInInput: SignInInput) => Promise<void>;
  signUp: (signUpParameters: SignUpParameters) => Promise<void>;
  confirmSignUp: (confirmSignUpInput: ConfirmSignUpInput) => Promise<void>;
  confirmEmail: ({ username, confirmationCode }: ConfirmSignUpInput) => Promise<void>;
  signOut: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
  resendVerificationCode: (username: string) => Promise<void>;
  deleteUser: () => Promise<void>;
  resetUserPassword: (username: string) => Promise<void>;
  confirmUserPasswordReset: (username: string, code: string, newPassword: string) => Promise<void>;
  updateUserPassword: (oldPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isUserSignedIn, setIsUserSignedIn] = useState(false);
  const [userDetails, setUserDetails] = useState<any>(null);

  const checkAuthStatus = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const groups = session.tokens?.idToken?.payload?.['cognito:groups'];
      setIsUserSignedIn(true);
      const userInfo = { 
        userId: currentUser.username, 
        email: currentUser.signInDetails?.loginId,
        role: Array.isArray(groups) && groups.includes('Admin') ? 'ADMIN' : 'PLAYER',
      };
      setUserDetails(userInfo);
      await fetchAndSetUserAttributes();
      void recordWebActivity().catch(() => undefined);
    } catch {
      setIsUserSignedIn(false);
      setUserDetails(null);
    }
  }, []);
  
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const signIn = async ({ username, password }: SignInInput) => {
    try {
      const result = await amplifySignIn({ username: username.trim(), password });
      if (!result.isSignedIn) {
        if (result.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
          const verificationError = new Error('Your account requires verification.');
          verificationError.name = 'UserNotConfirmedException';
          throw verificationError;
        }
        if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          throw new Error('This account requires a permanent password before it can sign in.');
        }
        throw new Error(`Additional sign-in step required: ${result.nextStep.signInStep}`);
      }
      await fetchAuthSession({ forceRefresh: true });
      const fetchedAttributes = await fetchUserAttributes();
      const isEmailVerified = fetchedAttributes.email_verified === 'true';
  
      if (!isEmailVerified) {
        throw new Error('Please verify your email to continue.');
      }
  
      if (fetchedAttributes.preferred_username && fetchedAttributes.given_name && fetchedAttributes.family_name) {
        try {
          await updateMyProfile(
            fetchedAttributes.preferred_username,
            fetchedAttributes.given_name,
            fetchedAttributes.family_name,
          );
        } catch (profileError) {
          // Authentication has already succeeded. A temporary profile-service
          // problem must not leave the modal claiming that login failed.
          console.warn('Signed in, but profile synchronization was deferred:', profileError);
        }
      }

      await applyPendingLifecycleConsent(username).catch(() => undefined);

      setIsUserSignedIn(true);
      await checkAuthStatus();
    } catch (error) {
      console.error('Error during sign in or fetching user attributes:', error);
      throw error; 
    }
  };
  
  const signUp = async ({
    username,
    password,
    preferred_username,
    given_name,
    family_name,
    lifecycleEmailOptIn = false,
  }: SignUpParameters) => {
    try {
      await amplifySignUp({
        username: username.trim(),
        password,
        options: {
          userAttributes: {
            email: username.trim(),
            preferred_username,
            given_name,
            family_name,
          },
          autoSignIn: { enabled: true }
        }
      });
      rememberPendingLifecycleConsent(username, lifecycleEmailOptIn);
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  };

  const confirmSignUp = async ({
    username,
    confirmationCode
  }: ConfirmSignUpInput) => {
    try {
      await amplifyConfirmSignUp({
        username,
        confirmationCode
      });
    } catch (error) {
      console.error('Error confirming sign up:', error);
      throw error;
    }
  };

  const confirmEmail = async ({ username, confirmationCode }: ConfirmSignUpInput) => {
    try {
      await confirmSignUp({
        username,
        confirmationCode,
      });
      try {
        await autoSignIn();
        await checkAuthStatus();
        await applyPendingLifecycleConsent(username);
      } catch { /* Verification succeeded; the pending preference is applied after normal sign-in. */ }
    } catch (error) {
      console.error("Error confirming sign up:", error);
      throw error; 
    }
  };
  
  const signOut = async () => {
    try {
      await amplifySignOut();
      setIsUserSignedIn(false);
      setUserDetails(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const fetchAndSetUserAttributes = async () => {
    try {
      const fetchedAttributes = await fetchUserAttributes();
      setUserDetails((prevDetails: any) => ({
        ...prevDetails,
        ...fetchedAttributes,
      }));
    } catch (error) {
      console.error('Error fetching user attributes:', error);
    }
  };

  const resendVerificationCode = async (username: string) => {
    try {
      await resendSignUpCode({ username });
      console.log('Resend verification code successful');
    } catch (error) {
      console.error('Error resending verification code:', error);
      throw error;
    }
  };

  async function deleteUser() {
    try {
      await deleteLifecycleEmailData().catch(() => undefined);
      try {
        await deleteMyProfile();
      } catch (profileError) {
        console.warn('Could not remove the app profile before deleting Cognito user:', profileError);
      }
      await amplifyDeleteUser();
      await signOut();
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error; 
    }
  }

  const resetUserPassword = async (username: string) => {
    try {
      const output = await resetPassword({ username });
      // Handle the next steps based on the output if needed
      console.log(output);
    } catch (error) {
      console.error('Error resetting password:', error);
    }
  };

  const confirmUserPasswordReset = async (username: string, code: string, newPassword: string) => {
    try {
      await confirmResetPassword({ username, confirmationCode: code, newPassword });
      console.log('Password reset successfully.');
    } catch (error) {
      console.error('Error confirming password reset:', error);
    }
  };

  const updateUserPassword = async (oldPassword: string, newPassword: string) => {
    try {
      await updatePassword({ oldPassword, newPassword });
      console.log('Password updated successfully.');
    } catch (error) {
      console.error('Error updating password:', error);
    }
  };
  
  return (
    <AuthContext.Provider value={{ isUserSignedIn, signIn, userDetails, signUp, confirmSignUp, confirmEmail, signOut, checkAuthStatus, resendVerificationCode, deleteUser, resetUserPassword, confirmUserPasswordReset, updateUserPassword }}>
      {children}
    </AuthContext.Provider>
  );
};
